import React, { useCallback, useEffect, useRef, useState } from "react";
import { MdRefresh } from "react-icons/md";
import { FilterX, Search as SearchIcon, X } from "lucide-react";
import AdminDatePicker from "../../admin/AdminDatePicker";
import { useDispatch } from "react-redux";
import {
  getTransactionsApiErrorMessage,
  getTransactionsByDateRange,
  parseTransactionsResponse,
} from "../../../server/Transactions";
import { setPreload } from "../../../server/CartsOpen";
import {
  getPaymentStatusCssClass,
  getPaymentStatusText,
} from "../../../utils/paymentStatus";
import {
  getTransactionTypeCssClass,
  getTransactionTypeText,
} from "../../../utils/transactionType";
import { formatPrice } from "../../../utils/formatPrice";
import {
  sortTransactionRows,
  transactionsToRows,
} from "../../../utils/transactionRows";
import {
  formatTransactionDateTime,
  getDefaultPaymentsFromDate,
  getPickerDateRange,
} from "../../../utils/paymentsDateUtils";
import {
  resolveApiBrowseFilters,
  resolveCommittedOrderId,
  toServerId,
} from "../../../utils/paymentsBrowseUtils";

const TX_PAGE_SIZE = 100;
const SCROLL_THRESHOLD_PX = 400;

/** Merge two transaction rows sorted newest-first (desc by date). */
const mergeSortedTransactionRows = (left, right) => {
  const sortedRight = sortTransactionRows(right);
  if (!left.length) return sortedRight;

  const merged = [];
  let i = 0;
  let j = 0;
  while (i < left.length && j < sortedRight.length) {
    const ta = new Date(left[i].date || 0).getTime();
    const tb = new Date(sortedRight[j].date || 0).getTime();
    if (ta >= tb) merged.push(left[i++]);
    else merged.push(sortedRight[j++]);
  }
  return merged.concat(left.slice(i), sortedRight.slice(j));
};

const PAYMENT_STATUS_FILTER_OPTIONS = [
  { value: "", label: "כל הסטטוסים" },
  { value: "SUCCESS", label: getPaymentStatusText("SUCCESS") },
  { value: "FAILED", label: getPaymentStatusText("FAILED") },
  { value: "PENDING", label: getPaymentStatusText("PENDING") },
];

const TRANSACTION_TYPE_FILTER_OPTIONS = [
  { value: "", label: "כל סוגי הפעולות" },
  { value: "PAYMENT", label: getTransactionTypeText("PAYMENT") },
  { value: "CHARGE", label: getTransactionTypeText("CHARGE") },
  { value: "REFUND", label: getTransactionTypeText("REFUND") },
];

const Payments = () => {
  const dispatch = useDispatch();

  const [date, setDate] = useState(getDefaultPaymentsFromDate);
  const [toDate, setToDate] = useState(new Date());
  const [rows, setRows] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [searchOrderId, setSearchOrderId] = useState("");
  const [committedOrderId, setCommittedOrderId] = useState("");
  const [filterPaymentStatus, setFilterPaymentStatus] = useState("");
  const [filterTransactionType, setFilterTransactionType] = useState("");
  const [loadError, setLoadError] = useState(null);
  const [loadMoreError, setLoadMoreError] = useState(null);
  const [isLoadPaused, setIsLoadPaused] = useState(false);
  const [openDatePickerId, setOpenDatePickerId] = useState(null);

  const sentinelRef = useRef(null);
  const tableScrollRef = useRef(null);
  const loadingMoreRef = useRef(false);
  const hasMoreRef = useRef(false);
  const dateRangeRef = useRef({ fromDate: "", toDate: "" });
  const nextCursorRef = useRef(null);
  const fetchGenerationRef = useRef(0);
  const isLoadPausedRef = useRef(false);
  const searchOrderIdRef = useRef("");
  const browseFiltersRef = useRef({
    status: "",
    orderId: "",
    transactionType: "",
  });

  searchOrderIdRef.current = searchOrderId;

  const buildBrowseFilters = useCallback(
    (overrides = {}) => ({
      status:
        overrides.status !== undefined ? overrides.status : filterPaymentStatus,
      orderId:
        overrides.orderId !== undefined ? overrides.orderId : committedOrderId,
      transactionType:
        overrides.transactionType !== undefined
          ? overrides.transactionType
          : filterTransactionType,
    }),
    [filterPaymentStatus, committedOrderId, filterTransactionType],
  );

  const syncBrowseFilters = useCallback(
    (overrides = {}) => {
      browseFiltersRef.current = buildBrowseFilters(overrides);
      return browseFiltersRef.current;
    },
    [buildBrowseFilters],
  );

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  useEffect(() => {
    isLoadPausedRef.current = isLoadPaused;
  }, [isLoadPaused]);

  const syncHasMore = useCallback((more) => {
    setHasMore(more);
    hasMoreRef.current = more;
    return more;
  }, []);

  const fetchTransactionsPage = useCallback(
    async (reset = false, filters = browseFiltersRef.current, generation) => {
      const { fromDate, toDate } = dateRangeRef.current;
      const { status, orderId, transactionType } = filters;
      const response = await getTransactionsByDateRange({
        fromDate,
        toDate,
        limit: TX_PAGE_SIZE,
        cursor: reset ? undefined : nextCursorRef.current,
        ...resolveApiBrowseFilters({ status, orderId, transactionType }),
      });

      if (
        generation !== undefined &&
        generation !== fetchGenerationRef.current
      ) {
        return false;
      }

      const txs = parseTransactionsResponse(response.data);
      const newRows = transactionsToRows(txs);

      setRows((prev) =>
        reset
          ? sortTransactionRows(newRows)
          : mergeSortedTransactionRows(prev, newRows),
      );

      const nextCursor = response.data?.nextCursor ?? null;
      nextCursorRef.current = nextCursor;
      syncHasMore(response.data?.hasMore ?? Boolean(nextCursor));
      return txs.length > 0 || Boolean(nextCursor);
    },
    [syncHasMore],
  );

  const loadMore = useCallback(async () => {
    if (
      loadingMoreRef.current ||
      !hasMoreRef.current ||
      isLoadPausedRef.current
    ) {
      return;
    }

    const generation = fetchGenerationRef.current;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    setLoadMoreError(null);

    try {
      await fetchTransactionsPage(false, browseFiltersRef.current, generation);
    } catch (err) {
      if (generation !== fetchGenerationRef.current) return;
      console.error("error loading more payments:", err);
      setLoadMoreError(getTransactionsApiErrorMessage(err));
      setIsLoadPaused(true);
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [fetchTransactionsPage]);

  const retryLoadMore = useCallback(() => {
    if (!nextCursorRef.current) return;
    setLoadMoreError(null);
    isLoadPausedRef.current = false;
    setIsLoadPaused(false);
    loadMore();
  }, [loadMore]);

  const initBrowse = useCallback(
    async (dateRange, filters) => {
      const generation = ++fetchGenerationRef.current;
      const activeFilters = syncBrowseFilters(filters);

      dateRangeRef.current = dateRange ?? dateRangeRef.current;
      dispatch(setPreload(true));
      setInitialLoading(true);
      setRows([]);
      setLoadError(null);
      setLoadMoreError(null);
      setIsLoadPaused(false);
      nextCursorRef.current = null;
      syncHasMore(false);

      try {
        await fetchTransactionsPage(true, activeFilters, generation);
      } catch (err) {
        if (generation !== fetchGenerationRef.current) return;
        console.error("error loading payments:", err);
        setRows([]);
        setLoadError(getTransactionsApiErrorMessage(err));
        syncHasMore(false);
      } finally {
        if (generation === fetchGenerationRef.current) {
          setInitialLoading(false);
          dispatch(setPreload(false));
        }
      }
    },
    [dispatch, fetchTransactionsPage, syncBrowseFilters, syncHasMore],
  );

  const initBrowseRef = useRef(initBrowse);
  initBrowseRef.current = initBrowse;

  useEffect(() => {
    try {
      dateRangeRef.current = getPickerDateRange(date, toDate);
      initBrowseRef.current(dateRangeRef.current);
      tableScrollRef.current?.scrollTo(0, 0);
    } catch {
      setLoadError("תאריך לא תקין");
      setInitialLoading(false);
      dispatch(setPreload(false));
    }
  }, [date, toDate, dispatch]);

  const syncCommittedOrderInput = useCallback((orderId) => {
    setSearchOrderId(orderId);
    searchOrderIdRef.current = orderId;
  }, []);

  const commitOrderServerFilter = useCallback(() => {
    requestAnimationFrame(() => {
      const orderInput = searchOrderIdRef.current.trim();

      if (!orderInput) {
        if (!committedOrderId) return;
        setCommittedOrderId("");
        syncCommittedOrderInput("");
        setLoadError(null);
        setLoadMoreError(null);
        initBrowseRef.current(dateRangeRef.current, { orderId: "" });
        tableScrollRef.current?.scrollTo(0, 0);
        return;
      }

      const serverOrderId = toServerId(orderInput);
      if (!serverOrderId) {
        syncCommittedOrderInput(committedOrderId);
        return;
      }

      if (serverOrderId === committedOrderId) {
        syncCommittedOrderInput(serverOrderId);
        return;
      }

      setCommittedOrderId(serverOrderId);
      syncCommittedOrderInput(serverOrderId);
      setLoadError(null);
      setLoadMoreError(null);
      initBrowseRef.current(dateRangeRef.current, { orderId: serverOrderId });
      tableScrollRef.current?.scrollTo(0, 0);
    });
  }, [committedOrderId, syncCommittedOrderInput]);

  const runSearch = useCallback(async () => {
    const orderId = resolveCommittedOrderId(
      searchOrderIdRef.current,
      committedOrderId,
    );
    setCommittedOrderId(orderId);
    syncCommittedOrderInput(orderId);
    try {
      dateRangeRef.current = getPickerDateRange(date, toDate);
      await initBrowseRef.current(dateRangeRef.current, { orderId });
      setTimeout(() => tableScrollRef.current?.scrollTo(0, 0), 100);
    } catch {
      setLoadError("תאריך לא תקין");
      setInitialLoading(false);
      dispatch(setPreload(false));
    }
  }, [committedOrderId, date, toDate, syncCommittedOrderInput, dispatch]);

  const handleStatusFilterChange = useCallback(
    (e) => {
      const status = e.target.value;
      setFilterPaymentStatus(status);
      initBrowseRef.current(dateRangeRef.current, {
        status,
        orderId: committedOrderId,
        transactionType: filterTransactionType,
      });
      tableScrollRef.current?.scrollTo(0, 0);
    },
    [committedOrderId, filterTransactionType],
  );

  const handleTransactionTypeFilterChange = useCallback(
    (e) => {
      const transactionType = e.target.value;
      setFilterTransactionType(transactionType);
      initBrowseRef.current(dateRangeRef.current, {
        status: filterPaymentStatus,
        orderId: committedOrderId,
        transactionType,
      });
      tableScrollRef.current?.scrollTo(0, 0);
    },
    [committedOrderId, filterPaymentStatus],
  );

  const clearOrderSearch = useCallback(() => {
    setSearchOrderId("");
    if (!committedOrderId) return;
    setCommittedOrderId("");
    setLoadError(null);
    setLoadMoreError(null);
    initBrowseRef.current(dateRangeRef.current, { orderId: "" });
  }, [committedOrderId]);

  const clearFilters = () => {
    setSearchOrderId("");
    setFilterPaymentStatus("");
    setFilterTransactionType("");
    setCommittedOrderId("");
    setLoadError(null);
    setLoadMoreError(null);
    initBrowseRef.current(dateRangeRef.current, {
      status: "",
      orderId: "",
      transactionType: "",
    });
  };

  const hasActiveFilters = Boolean(
    filterPaymentStatus || filterTransactionType || committedOrderId,
  );

  const noDataHint = hasActiveFilters
    ? "נסה לשנות או לנקות את הסינונים, או להרחיב את טווח התאריכים"
    : "נסה להרחיב את טווח התאריכים";

  const showNoData = !initialLoading && !loadError && !rows.length;
  const showTable = !initialLoading && !loadError && rows.length > 0;

  useEffect(() => {
    const scrollEl = tableScrollRef.current;
    const sentinel = sentinelRef.current;
    if (
      !scrollEl ||
      !sentinel ||
      !hasMore ||
      initialLoading ||
      !showTable ||
      isLoadPaused
    ) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries.some((entry) => entry.isIntersecting) &&
          !loadingMoreRef.current &&
          !isLoadPausedRef.current &&
          hasMoreRef.current
        ) {
          loadMore();
        }
      },
      { root: scrollEl, rootMargin: `${SCROLL_THRESHOLD_PX}px` },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, initialLoading, loadMore, showTable, isLoadPaused]);

  return (
    <div className="payments-page admin-page admin-page--fill">
      <div className="payments-chrome">
        <header className="admin-page__header">
          <div className="admin-page__header-start">
            <h1 className="title-role">פירוט תשלומים</h1>
          </div>
          <div className="admin-page__header-end">
            <button
              type="button"
              className="admin-page__icon-btn"
              onMouseDown={(e) => e.preventDefault()}
              onClick={runSearch}
              title="רענן"
              aria-label="רענן נתונים"
            >
              <MdRefresh />
            </button>
          </div>
        </header>

        <div className="payments-filters admin-filter-bar admin-filter-bar--spread">
          <div className="admin-filter-bar__primary payments-filters__row">
            <AdminDatePicker
              label="מתאריך"
              value={date}
              onChange={setDate}
              maxDate={toDate}
              openPickerId={openDatePickerId}
              onOpenPickerIdChange={setOpenDatePickerId}
            />

            <AdminDatePicker
              label="עד תאריך"
              value={toDate}
              onChange={setToDate}
              minDate={date}
              openPickerId={openDatePickerId}
              onOpenPickerIdChange={setOpenDatePickerId}
            />

            <div className="search admin-search-affix">
              <input
                onChange={(e) => setSearchOrderId(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    commitOrderServerFilter();
                  }
                }}
                onBlur={commitOrderServerFilter}
                value={searchOrderId}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="מספר הזמנה..."
                className="admin-input"
                aria-label="חיפוש לפי מספר הזמנה"
              />
              {searchOrderId ? (
                <button
                  type="button"
                  className="admin-search-affix__clear"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={clearOrderSearch}
                  aria-label="נקה חיפוש הזמנה"
                  title="נקה חיפוש"
                >
                  <X aria-hidden size={18} strokeWidth={2} />
                </button>
              ) : (
                <span className="admin-search-affix__icon" aria-hidden>
                  <SearchIcon size={18} strokeWidth={2} />
                </span>
              )}
            </div>

            <select
              className="admin-input"
              value={filterTransactionType}
              onChange={handleTransactionTypeFilterChange}
              aria-label="סוג פעולה"
            >
              {TRANSACTION_TYPE_FILTER_OPTIONS.map((option) => (
                <option key={option.value || "all-types"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            <select
              className="admin-input"
              value={filterPaymentStatus}
              onChange={handleStatusFilterChange}
              aria-label="סטטוס תשלום"
            >
              {PAYMENT_STATUS_FILTER_OPTIONS.map((option) => (
                <option key={option.value || "all"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            <button
              type="button"
              className="clear-filters-btn admin-icon-btn admin-icon-btn--ghost admin-filter-end"
              onClick={clearFilters}
              title="נקה סינונים"
              aria-label="נקה סינונים"
            >
              <FilterX aria-hidden size={18} strokeWidth={2} />
            </button>
          </div>
        </div>
      </div>

      <div className="payments-body">
        {initialLoading ? (
          <div className="payments-state payments-state--loading">
            <div className="payments-spinner" aria-hidden="true" />
            <p>טוען תשלומים...</p>
          </div>
        ) : null}

        {loadError ? (
          <div className="payments-state payments-state--error">
            <h2 className="payments-state__title">שגיאה בטעינת תשלומים</h2>
            <p className="payments-state__hint">{loadError}</p>
          </div>
        ) : null}

        {showNoData ? (
          <div className="payments-state">
            <h2 className="payments-state__title">לא נמצאו תשלומים</h2>
            <p className="payments-state__hint">{noDataHint}</p>
          </div>
        ) : null}

        {showTable ? (
          <div className="payments-table-scroll" ref={tableScrollRef}>
            <table>
              <thead>
                <tr>
                  <th>מזהה תשלום</th>
                  <th># הזמנה</th>
                  <th>לקוח</th>
                  <th>תאריך ושעה</th>
                  <th>סוג פעולה</th>
                  <th>סכום</th>
                  <th>סטטוס</th>
                  <th>כרטיס</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const statusText = getPaymentStatusText(row.status);
                  const statusClass = getPaymentStatusCssClass(row.status);
                  const transactionTypeText = getTransactionTypeText(
                    row.transactionType,
                  );
                  const transactionTypeClass = getTransactionTypeCssClass(
                    row.transactionType,
                  );
                  const customerLabel =
                    row.customerLabel ||
                    (row.customerId ? `לקוח ${row.customerId}` : "-");

                  return (
                    <tr key={row.key}>
                      <td className="cell-mono" data-label="מזהה תשלום">
                        <p>{row.transactionId || "-"}</p>
                      </td>
                      <td className="cell-mono" data-label="# הזמנה">
                        <p>{row.orderId}</p>
                      </td>
                      <td data-label="לקוח">
                        <p>{customerLabel}</p>
                      </td>
                      <td data-label="תאריך ושעה">
                        <p>{formatTransactionDateTime(row.date)}</p>
                      </td>
                      <td data-label="סוג פעולה">
                        <span
                          className={`transaction-type transaction-type--${transactionTypeClass}`}
                        >
                          {transactionTypeText || "-"}
                        </span>
                      </td>
                      <td className="cell-amount" data-label="סכום">
                        <p className="price">
                          {row.sum != null ? `₪${formatPrice(row.sum)}` : "-"}
                        </p>
                      </td>
                      <td data-label="סטטוס">
                        <span
                          className={`payment-status payment-status--${statusClass}`}
                        >
                          {statusText || "-"}
                        </span>
                      </td>
                      <td className="cell-card" data-label="כרטיס">
                        <p>
                          {row.lastFourDigits
                            ? `****${row.lastFourDigits}`
                            : "-"}
                        </p>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div
              ref={sentinelRef}
              className="payments-sentinel"
              aria-hidden="true"
            />

            {loadingMore ? (
              <div className="payments-state payments-state--loading payments-state--inline">
                <div className="payments-spinner" aria-hidden="true" />
                <p>טוען תשלומים נוספים...</p>
              </div>
            ) : null}

            {loadMoreError ? (
              <div
                className="payments-state payments-state--error payments-state--inline"
                role="alert"
              >
                <p>{loadMoreError}</p>
                <button
                  type="button"
                  className="payments-retry-btn"
                  onClick={retryLoadMore}
                >
                  נסה שוב
                </button>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default Payments;
